import React, { useState, useEffect, useRef } from "react";
import { fabric } from "fabric";
import io from "socket.io-client";
import { AlertCircle, Wifi, WifiOff, Eye, EyeOff, RefreshCw } from "lucide-react";
import "./Viewer.css";
import config from "../config";

const Viewer = () => {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const socketRef = useRef(null);
  const containerRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const lastUpdateRef = useRef(0);
  const isLoadingSlide = useRef(false);
  const autoReconnectIntervalRef = useRef(null);
  const hasStartedConnection = useRef(false);

  const [isConnected, setIsConnected] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: 1024, height: 576 });
  const [debugVisible, setDebugVisible] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);
  const [usePolling, setUsePolling] = useState(false);
  const [presentationFound, setPresentationFound] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    serverUrl: config.serverUrl,
    clientUrl: config.clientUrl,
    socketId: null,
    connectionStatus: "desconectado",
    lastEvent: null,
    presentationStatus: "aguardando",
    slideCount: 0,
    transport: "unknown",
    connectionAttempts: 0,
    lastError: null,
    connectionMode: "socket.io",
    events: []
  });

  // Função para adicionar evento ao debug
  const addDebugEvent = (event, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    const eventWithType = `[${type.toUpperCase()}] ${timestamp}: ${event}`;
    
    setDebugInfo(prev => ({
      ...prev,
      lastEvent: event,
      events: [...prev.events.slice(-19), eventWithType]
    }));
    
    console.log(`[Viewer Debug] ${eventWithType}`);
  };

  // Testar conectividade com o servidor
  const testServerConnection = async () => {
    try {
      addDebugEvent("Testando conexão com servidor...", "info");
      const response = await fetch(`${config.serverUrl}/api/health`);
      const data = await response.json();
      addDebugEvent(`Servidor respondeu: ${JSON.stringify(data)}`, "success");
      return true;
    } catch (error) {
      addDebugEvent(`Erro ao testar servidor: ${error.message}`, "error");
      setDebugInfo(prev => ({ ...prev, lastError: error.message }));
      return false;
    }
  };

  // Função de polling HTTP como fallback
  const startPolling = () => {
    console.log("🔄 Iniciando modo polling HTTP...");
    setUsePolling(true);
    setDebugInfo(prev => ({ ...prev, connectionMode: "polling HTTP" }));
    addDebugEvent("Mudando para modo polling HTTP", "warning");
    
    // Registrar viewer
    fetch(`${config.serverUrl}/api/presentation/viewer/join`, {
      method: "POST"
    }).catch(err => console.error("Erro ao registrar viewer:", err));
    
    // Limpar intervalo anterior se existir
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    // Flag para controlar primeira carga
    let firstLoad = true;
    
    // Função de polling
    const pollPresentation = async () => {
      try {
        const response = await fetch(
          `${config.serverUrl}/api/presentation/poll/pde?lastUpdate=${lastUpdateRef.current}`
        );
        
        if (response.status === 304) {
          // Sem mudanças
          return;
        }
        
        const data = await response.json();
        
        if (data.status === "active") {
          setPresentationFound(true);
          
          if (data.lastUpdate > lastUpdateRef.current || firstLoad) {
            if (!firstLoad) {
              addDebugEvent(`Atualização recebida via polling`, "info");
            }
            lastUpdateRef.current = data.lastUpdate;
          }
          
          // Atualizar slides
          setSlides(data.slides);
          
          // Carregar slide inicial ou mudanças
          if (firstLoad || data.currentSlide !== currentSlide) {
            if (firstLoad) {
              addDebugEvent(`Carregamento inicial via polling - Slide: ${data.currentSlide + 1}/${data.slides.length}`, "info");
              firstLoad = false;
            } else {
              addDebugEvent(`Mudança de slide detectada: ${currentSlide} → ${data.currentSlide}`, "info");
            }
            setCurrentSlide(data.currentSlide);
            // Carregar o slide
            if (data.slides[data.currentSlide]) {
              loadSlide(data.slides[data.currentSlide], data.currentSlide);
            }
          }
          
          setLoading(false);
          setError("");
          setIsConnected(true);
          
          setDebugInfo(prev => ({
            ...prev,
            presentationStatus: "ativa",
            slideCount: data.slides.length,
            connectionStatus: "conectado (polling)"
          }));
          
        } else if (data.status === "not_found") {
          setPresentationFound(false);
          if (!error) {
            setError("Nenhuma apresentação ativa");
            setLoading(false);
            setDebugInfo(prev => ({
              ...prev,
              presentationStatus: "não encontrada"
            }));
          }
        }
      } catch (error) {
        console.error("❌ Erro no polling:", error);
        if (connectionAttempts > 10) {
          addDebugEvent(`Erro no polling: ${error.message}`, "error");
        }
      }
    };
    
    // Fazer primeira chamada imediatamente
    pollPresentation();
    
    // Polling a cada 2 segundos
    pollingIntervalRef.current = setInterval(pollPresentation, 2000);
  };

  // Calcular tamanho responsivo
  useEffect(() => {
    const calculateSize = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth - 80;
      const containerHeight = container.clientHeight - 80;

      const aspectRatio = 16 / 9;
      let width = containerWidth;
      let height = width / aspectRatio;

      if (height > containerHeight) {
        height = containerHeight;
        width = height * aspectRatio;
      }

      if (width > 1024) {
        width = 1024;
        height = 576;
      }

      setCanvasSize({ width, height });
    };

    calculateSize();
    window.addEventListener("resize", calculateSize);
    return () => window.removeEventListener("resize", calculateSize);
  }, []);

  // Conectar ao Socket
  const connectSocket = () => {
    if (socketRef.current?.connected) {
      addDebugEvent("Socket já conectado, pulando reconexão", "info");
      return;
    }

    setConnectionAttempts(prev => prev + 1);
    addDebugEvent(`Tentativa de conexão Socket.io #${connectionAttempts + 1}`, "info");

    // Configurações específicas para Heroku + Vercel
    const socketOptions = {
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      forceNew: true,
      path: "/socket.io/",
      secure: true,
      rejectUnauthorized: false
    };

    addDebugEvent(`Conectando com opções: ${JSON.stringify(socketOptions)}`, "info");
    
    socketRef.current = io(config.serverUrl, socketOptions);

    // Debug do transporte
    socketRef.current.on("connect", () => {
      const transport = socketRef.current.io.engine.transport.name;
      const socketId = socketRef.current.id;
      
      console.log("✅ Socket conectado!");
      setIsConnected(true);
      setConnectionAttempts(0);
      setUsePolling(false);
      
      setDebugInfo(prev => ({
        ...prev,
        socketId,
        connectionStatus: "conectado",
        transport,
        connectionAttempts: 0,
        connectionMode: "socket.io"
      }));
      
      addDebugEvent(`Socket conectado! ID: ${socketId}, Transport: ${transport}`, "success");
      
      // Limpar polling se estiver ativo
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      // Entrar na apresentação
      console.log("👁️ Entrando na apresentação...");
      addDebugEvent("Solicitando entrada na apresentação 'pde'", "info");
      socketRef.current.emit("join-presentation", "pde");
    });

    socketRef.current.on("disconnect", (reason) => {
      console.log("❌ Socket desconectado:", reason);
      setIsConnected(false);
      setDebugInfo(prev => ({
        ...prev,
        connectionStatus: "desconectado",
        socketId: null,
        transport: "unknown"
      }));
      addDebugEvent(`Socket desconectado: ${reason}`, "error");
    });

    socketRef.current.on("connect_error", (error) => {
      console.error("❌ Erro de conexão:", error);
      addDebugEvent(`Erro de conexão: ${error.message} (${error.type})`, "error");
      setDebugInfo(prev => ({
        ...prev,
        connectionStatus: "erro",
        lastError: error.message,
        connectionAttempts: connectionAttempts + 1
      }));
    });

    socketRef.current.on("connect_timeout", () => {
      addDebugEvent("Timeout na conexão Socket.io", "error");
    });

    socketRef.current.on("reconnect_attempt", (attemptNumber) => {
      addDebugEvent(`Tentativa de reconexão Socket.io #${attemptNumber}`, "warning");
    });

    socketRef.current.on("reconnect_failed", () => {
      addDebugEvent("Falha em todas tentativas de reconexão Socket.io", "error");
      // Mudar para polling
      if (!usePolling) {
        addDebugEvent("Mudando para modo polling HTTP", "warning");
        startPolling();
      }
    });

    socketRef.current.on("presentation-data", (data) => {
      console.log("📊 Dados recebidos:", data);
      addDebugEvent("Dados da apresentação recebidos via Socket.io", "success");
      setLoading(false);
      setError("");
      setPresentationFound(true);
      
      // Limpar polling se estiver ativo
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      if (data.presentation && data.presentation.slides) {
        const slideCount = data.presentation.slides.length;
        const slideIndex = data.currentSlide !== undefined ? data.currentSlide : (data.presentation.currentSlide || 0);
        
        setSlides(data.presentation.slides);
        setCurrentSlide(slideIndex);
        
        setDebugInfo(prev => ({
          ...prev,
          presentationStatus: "ativa",
          slideCount
        }));
        
        addDebugEvent(`Apresentação carregada: ${slideCount} slides, slide atual: ${slideIndex + 1}`, "success");
        
        // Carregar slide inicial
        if (data.presentation.slides[slideIndex]) {
          console.log(`📄 Carregando slide inicial: ${slideIndex}`);
          loadSlide(data.presentation.slides[slideIndex], slideIndex);
        }
      }
    });

    socketRef.current.on("presentation-not-found", () => {
      console.log("❌ Apresentação não encontrada");
      setPresentationFound(false);
      setError("Nenhuma apresentação ativa");
      setLoading(false);
      setDebugInfo(prev => ({
        ...prev,
        presentationStatus: "não encontrada"
      }));
      addDebugEvent("Apresentação não encontrada", "warning");
    });

    socketRef.current.on("slide-changed", (data) => {
      console.log("🔄 Slide mudou:", data.slideIndex);
      const newIndex = data.slideIndex;
      setCurrentSlide(newIndex);
      addDebugEvent(`Slide mudou para: ${newIndex + 1}`, "info");
      
      if (slides[newIndex]) {
        loadSlide(slides[newIndex], newIndex);
      }
    });

    socketRef.current.on("slide-updated", (data) => {
      console.log("📝 Slide atualizado:", data.slideIndex);
      addDebugEvent(`Slide ${data.slideIndex + 1} atualizado`, "info");
      
      // Se veio com conteúdo completo, usar esse
      let newContent = data.slideContent || JSON.stringify(data.slideData);
      
      // Atualizar array de slides
      setSlides(prev => {
        const newSlides = [...prev];
        if (newSlides[data.slideIndex]) {
          newSlides[data.slideIndex] = {
            ...newSlides[data.slideIndex],
            content: newContent
          };
        }
        return newSlides;
      });
      
      // Se for o slide atual, recarregar
      if (data.slideIndex === currentSlide) {
        if (data.slideData && typeof data.slideData === 'object') {
          loadSlideData(data.slideData);
        } else if (newContent) {
          try {
            const slideData = JSON.parse(newContent);
            loadSlideData(slideData);
          } catch (e) {
            console.error("Erro ao parsear conteúdo do slide:", e);
          }
        }
      }
    });

    socketRef.current.on("presentation-ended", () => {
      console.log("🛑 Apresentação encerrada");
      setError("A apresentação foi encerrada");
      setIsConnected(false);
      setPresentationFound(false);
      setDebugInfo(prev => ({
        ...prev,
        presentationStatus: "encerrada"
      }));
      addDebugEvent("Apresentação encerrada pelo apresentador", "warning");
    });

    socketRef.current.on("error", (data) => {
      console.error("❌ Erro:", data);
      setError(data.message || "Erro de conexão");
      addDebugEvent(`Erro do servidor: ${data.message || "Erro de conexão"}`, "error");
    });
  };

  // Tentar conectar automaticamente
  const attemptConnection = () => {
    if (hasStartedConnection.current) return;
    
    hasStartedConnection.current = true;
    addDebugEvent("Iniciando tentativa de conexão automática", "info");
    
    testServerConnection().then(serverOk => {
      if (serverOk) {
        connectSocket();
        
        // Timeout para Socket.io - se não conectar, usar polling
        setTimeout(() => {
          if (!isConnected && !usePolling) {
            console.warn("⚠️ Socket.io timeout, mudando para polling...");
            addDebugEvent("Socket.io falhou após 5s, usando polling HTTP", "warning");
            startPolling();
          }
        }, 5000);
      } else {
        setError("Não foi possível conectar ao servidor");
        setLoading(false);
        // Tentar polling mesmo assim
        setTimeout(() => {
          startPolling();
        }, 2000);
      }
    });
  };

  // Inicializar Canvas e Socket
  useEffect(() => {
    if (!canvasRef.current) return;

    console.log("🎨 Inicializando viewer...");
    addDebugEvent("Inicializando viewer", "info");

    // Criar canvas
    const canvas = new fabric.Canvas(canvasRef.current, {
      width: canvasSize.width,
      height: canvasSize.height,
      backgroundColor: "#ffffff",
      selection: false,
      interactive: false,
    });

    fabricRef.current = canvas;

    // Aplicar zoom
    const scale = canvasSize.width / 1024;
    canvas.setZoom(scale);

    // Iniciar conexão automaticamente após 500ms
    setTimeout(() => {
      attemptConnection();
    }, 500);

    // Auto-reconexão a cada 2 segundos se não conectado
    autoReconnectIntervalRef.current = setInterval(() => {
      if (!isConnected && !presentationFound && loading && !hasStartedConnection.current) {
        console.log("🔄 Tentando reconexão automática...");
        attemptConnection();
      }
    }, 2000);

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (autoReconnectIntervalRef.current) {
        clearInterval(autoReconnectIntervalRef.current);
      }
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (fabricRef.current) {
        fabricRef.current.dispose();
      }
      
      // Notificar saída se usando polling
      if (usePolling) {
        fetch(`${config.serverUrl}/api/presentation/viewer/leave`, {
          method: "POST"
        }).catch(() => {});
      }
    };
  }, [canvasSize]);

  // Função para reconectar manualmente
  const handleManualReconnect = () => {
    addDebugEvent("Reconexão manual solicitada", "info");
    setLoading(true);
    setError("");
    setConnectionAttempts(0);
    hasStartedConnection.current = false;
    
    // Limpar conexões existentes
    if (socketRef.current) {
      socketRef.current.disconnect();
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    setUsePolling(false);
    
    setTimeout(() => {
      attemptConnection();
    }, 500);
  };

  // Carregar slide
  const loadSlide = (slide, index) => {
    if (!slide || !fabricRef.current || isLoadingSlide.current) return;
    
    console.log("📄 Carregando slide:", index);
    addDebugEvent(`Carregando slide ${index + 1}`, "info");
    
    isLoadingSlide.current = true;
    
    // Limpar canvas primeiro
    fabricRef.current.clear();
    fabricRef.current.backgroundColor = "#ffffff";
    
    // Verificar se o conteúdo não é apenas um template vazio
    const emptyTemplates = [
      '{}',
      '{"objects":[],"version":"5.3.0"}',
      '{"objects":[]}'
    ];
    
    if (slide.content && slide.content !== '' && !emptyTemplates.includes(slide.content.trim())) {
      try {
        let slideData;
        // Verificar se o conteúdo já é um objeto ou precisa ser parseado
        if (typeof slide.content === 'string') {
          slideData = JSON.parse(slide.content);
        } else {
          slideData = slide.content;
        }
        
        // Verificar se tem objetos no slide
        if (slideData.objects && slideData.objects.length > 0) {
          console.log(`📄 Slide tem ${slideData.objects.length} objetos`);
          addDebugEvent(`Slide ${index + 1} tem ${slideData.objects.length} objetos`, "info");
        } else {
          console.log("📄 Slide vazio (sem objetos)");
          addDebugEvent(`Slide ${index + 1} está vazio`, "warning");
        }
        
        loadSlideData(slideData);
      } catch (error) {
        console.error("❌ Erro ao carregar slide:", error);
        console.error("❌ Conteúdo do slide:", slide.content);
        addDebugEvent(`Erro ao carregar slide: ${error.message}`, "error");
        fabricRef.current.renderAll();
        isLoadingSlide.current = false;
      }
    } else {
      // Slide vazio
      console.log("📄 Slide vazio");
      fabricRef.current.renderAll();
      isLoadingSlide.current = false;
      addDebugEvent(`Slide ${index + 1} está vazio`, "warning");
    }
  };

  // Carregar dados do slide
  const loadSlideData = (slideData) => {
    if (!fabricRef.current || !slideData) {
      isLoadingSlide.current = false;
      return;
    }
    
    try {
      console.log("🎨 Carregando dados do slide no canvas...");
      console.log("📊 Dados:", slideData);
      
      fabricRef.current.loadFromJSON(slideData, () => {
        // Desabilitar interação
        fabricRef.current.forEachObject((obj) => {
          obj.selectable = false;
          obj.evented = false;
        });
        
        // Aplicar zoom
        const scale = canvasSize.width / 1024;
        fabricRef.current.setZoom(scale);
        fabricRef.current.renderAll();
        isLoadingSlide.current = false;
        
        console.log("✅ Slide carregado com sucesso");
        addDebugEvent("Slide renderizado no canvas", "success");
      });
    } catch (error) {
      console.error("❌ Erro ao processar dados do slide:", error);
      isLoadingSlide.current = false;
    }
  };

  // Atualizar zoom quando mudar tamanho
  useEffect(() => {
    if (fabricRef.current && !isLoadingSlide.current) {
      const scale = canvasSize.width / 1024;
      fabricRef.current.setDimensions({
        width: canvasSize.width,
        height: canvasSize.height,
      });
      fabricRef.current.setZoom(scale);
      fabricRef.current.renderAll();
    }
  }, [canvasSize]);

  if (loading) {
    return (
      <div className="viewer-container">
        <div className="viewer-loading">
          <div className="spinner"></div>
          <h2>Conectando à apresentação...</h2>
          <p>Aguarde um momento...</p>
          <p style={{ fontSize: "12px", color: "#999", marginTop: "10px" }}>
            Tentando conexão automática...
          </p>
          
          {/* Debug Panel */}
          {debugVisible && (
            <div className="debug-panel">
              <h3>🔧 Debug Info</h3>
              <div className="debug-info">
                <div><strong>Server URL:</strong> {debugInfo.serverUrl}</div>
                <div><strong>Client URL:</strong> {debugInfo.clientUrl}</div>
                <div><strong>Socket ID:</strong> {debugInfo.socketId || "Não conectado"}</div>
                <div><strong>Status:</strong> {debugInfo.connectionStatus}</div>
                <div><strong>Modo:</strong> {debugInfo.connectionMode}</div>
                <div><strong>Transport:</strong> {debugInfo.transport}</div>
                <div><strong>Tentativas:</strong> {debugInfo.connectionAttempts}</div>
                <div><strong>Apresentação:</strong> {debugInfo.presentationStatus}</div>
                {debugInfo.lastError && (
                  <div style={{color: "#ff6b6b"}}><strong>Último erro:</strong> {debugInfo.lastError}</div>
                )}
              </div>
              <div className="debug-events">
                <strong>Eventos:</strong>
                {debugInfo.events.map((event, i) => (
                  <div key={i} className="debug-event" style={{
                    color: event.includes('[ERROR]') ? '#ff6b6b' : 
                           event.includes('[WARNING]') ? '#feca57' : 
                           event.includes('[SUCCESS]') ? '#48dbfb' : '#bbb'
                  }}>{event}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viewer-container">
        <div className="viewer-error">
          <AlertCircle size={48} />
          <h2>Oops!</h2>
          <p>{error}</p>
          <p className="error-hint">
            Verifique se o apresentador iniciou a transmissão
          </p>
          
          <button 
            onClick={handleManualReconnect}
            style={{
              marginTop: "20px",
              padding: "10px 20px",
              background: "#3498db",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            <RefreshCw size={16} />
            Tentar Novamente
          </button>
          
          <a href="/" className="btn-home" style={{ marginTop: "10px" }}>
            Voltar ao início
          </a>
          
          {/* Debug Panel */}
          {debugVisible && (
            <div className="debug-panel" style={{ marginTop: "30px" }}>
              <h3>🔧 Debug Info</h3>
              <div className="debug-info">
                <div><strong>Server URL:</strong> {debugInfo.serverUrl}</div>
                <div><strong>Client URL:</strong> {debugInfo.clientUrl}</div>
                <div><strong>Socket ID:</strong> {debugInfo.socketId || "Não conectado"}</div>
                <div><strong>Status:</strong> {debugInfo.connectionStatus}</div>
                <div><strong>Modo:</strong> {debugInfo.connectionMode}</div>
                <div><strong>Transport:</strong> {debugInfo.transport}</div>
                <div><strong>Erro:</strong> {error}</div>
                {debugInfo.lastError && (
                  <div style={{color: "#ff6b6b"}}><strong>Detalhes:</strong> {debugInfo.lastError}</div>
                )}
              </div>
              <div className="debug-events">
                <strong>Últimos eventos:</strong>
                {debugInfo.events.map((event, i) => (
                  <div key={i} className="debug-event" style={{
                    color: event.includes('[ERROR]') ? '#ff6b6b' : 
                           event.includes('[WARNING]') ? '#feca57' : 
                           event.includes('[SUCCESS]') ? '#48dbfb' : '#bbb'
                  }}>{event}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-container">
      <div className="viewer-header">
        <div className="viewer-info">
          <h1 className="viewer-logo">Link2.do</h1>
          <span className="presentation-id">Apresentação ao vivo</span>
        </div>

        <div className="viewer-status">
          <button
            onClick={() => setDebugVisible(!debugVisible)}
            className="btn-icon"
            style={{ 
              background: "rgba(255,255,255,0.1)", 
              border: "1px solid rgba(255,255,255,0.2)",
              color: "white",
              padding: "8px",
              borderRadius: "4px",
              marginRight: "10px"
            }}
            title="Toggle Debug"
          >
            {debugVisible ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>

          <button
            onClick={handleManualReconnect}
            className="btn-icon"
            style={{ 
              background: "rgba(255,255,255,0.1)", 
              border: "1px solid rgba(255,255,255,0.2)",
              color: "white",
              padding: "8px",
              borderRadius: "4px",
              marginRight: "20px"
            }}
            title="Reconectar"
          >
            <RefreshCw size={20} />
          </button>

          <div className={`connection-status ${isConnected ? "connected" : "disconnected"}`}>
            {isConnected ? <Wifi size={16} /> : <WifiOff size={16} />}
            {isConnected ? `Ao vivo ${usePolling ? '(HTTP)' : ''}` : "Reconectando..."}
          </div>
          <span className="slide-indicator">
            Slide {currentSlide + 1} de {slides.length || 1}
          </span>
        </div>
      </div>

      <div className="viewer-content" ref={containerRef}>
        <div className="canvas-viewer" style={{ width: canvasSize.width, height: canvasSize.height }}>
          <canvas ref={canvasRef} />
        </div>
      </div>

      <div className="viewer-footer">
        <div className="slide-progress">
          <div
            className="progress-bar"
            style={{ width: `${((currentSlide + 1) / (slides.length || 1)) * 100}%` }}
          />
        </div>
      </div>

      {/* Debug Panel flutuante */}
      {debugVisible && (
        <div style={{
          position: "fixed",
          bottom: "60px",
          right: "20px",
          width: "400px",
          maxHeight: "300px",
          overflowY: "auto",
          background: "rgba(0, 0, 0, 0.9)",
          border: "1px solid #444",
          borderRadius: "8px",
          padding: "15px",
          fontSize: "12px",
          fontFamily: "monospace",
          color: "white",
          zIndex: 1000
        }}>
          <h4 style={{ margin: "0 0 10px 0", color: "#3498db" }}>🔧 Debug Info</h4>
          <div style={{ marginBottom: "10px" }}>
            <div><strong>Server:</strong> {debugInfo.serverUrl}</div>
            <div><strong>Socket ID:</strong> {debugInfo.socketId || "Não conectado"}</div>
            <div><strong>Modo:</strong> {debugInfo.connectionMode}</div>
            <div><strong>Transport:</strong> {debugInfo.transport}</div>
            <div><strong>Status:</strong> {debugInfo.connectionStatus}</div>
            <div><strong>Apresentação:</strong> {debugInfo.presentationStatus}</div>
            <div><strong>Slides:</strong> {debugInfo.slideCount}</div>
            <div><strong>Slide atual:</strong> {currentSlide + 1}</div>
            {debugInfo.lastError && (
              <div style={{color: "#ff6b6b"}}><strong>Erro:</strong> {debugInfo.lastError}</div>
            )}
          </div>
          <div style={{ 
            maxHeight: "150px", 
            overflowY: "auto",
            borderTop: "1px solid #444",
            paddingTop: "10px"
          }}>
            <strong>Eventos:</strong>
            {debugInfo.events.map((event, i) => (
              <div key={i} style={{ 
                color: event.includes('[ERROR]') ? '#ff6b6b' : 
                       event.includes('[WARNING]') ? '#feca57' : 
                       event.includes('[SUCCESS]') ? '#48dbfb' : '#bbb',
                marginBottom: "2px" 
              }}>{event}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Viewer;